import {
  Badge,
  Button,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@invent/wl-ui-kit-next/ui';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { NotPermitted } from '@/design';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import type { IOversightResponseRow } from '../../api/oversight-contracts';
import type {
  IAdminParticipantDetail,
  IAdminParticipantHub,
  IAdminParticipantIdentifier,
  IAdminParticipantSecAdvFirm,
  RecordType
} from '../../api/participant-contracts';
import { absent } from '../../components/account-facts';
import { Section } from '../../components/admin-page';
import { FirmLink } from '../../components/firm-link';
import { ParticipantLink, RECORD_TYPE_WORDS } from '../../components/participant-link';
import { RecyclePress } from '../../components/recycle-press';
import { Progress, ResponseStatusChip } from '../../components/responses-table';
import { responsePath } from '../../paths';
import { inTheBin, subjectOf } from '../../recycle-bin';
import { SEC_ADV_MATCHED_ID_TYPES } from '../../registries';
import { NOT_PERMITTED_DECIDE, NOT_PERMITTED_RAISE } from '../compliance/work-item-writes';
import { SecAdvRegisterEntry } from '../sec-adv-firm-page';
import { liveFirst, surveysOf } from './household';
import { hubSections, type IHubContext, type IHubSection } from './hub-sections';
import { AffiliationsFact, MembershipsFact } from './memberships';
import type { IAskEveryNumber } from './numbers';
import { ResearchHeadline } from './research-headline';
import { SurveyAnswersSection } from './survey-answers';
import { SurveysSection } from './surveys';

/**
 * The record pages (SR-391, design `2026-10-07-record-pages-person-team-firm.md`): the hub of a
 * person, a team or an RIA firm, drawn as **the record opened and nobody else**, with **one tab
 * per survey it holds** beside the record's own tab. The client found the household's page
 * chaotic — a person whose login also acts for their team got every section twice, and every
 * survey's staff fields one after another two levels down — so a record page draws only what
 * survey-api keys by the record on the record tab, and what it keys by the record and a survey on
 * that survey's tab. A team's record tab leads with its Advisors and routing, a firm's with its
 * SEC ADV register entry and Principals. An `org` holding neither survey keeps the household's
 * page.
 *
 * **The rule is the wire's key, never a judgement here** (§2): a section is the survey tab's when
 * survey-api keys its data by survey — the staff fields (every scope is per survey), the ranking
 * placements, the figures and that survey's responses — and the record tab's otherwise. Nothing
 * moves a value across that line.
 *
 * **Narrowed on this side, from the one document** (`recordView`): the hub carries the household's
 * cards, responses, notes, disclosures and staff-field records, and a record page keeps the
 * opened participant's alone. The others an account of theirs acts for are not dropped: Related
 * records names each with its kind and surveys, a link to its own page, beside the teams and firms
 * whose roster names the person. Two lists stay the household's because survey-api merges them:
 * the activity feed and the compliance history (the design's B2, asked and optional).
 *
 * **A survey's current response is its newest live one** (SR-470, design
 * `2026-10-24-recycle-bin-ui.md`, D7): `recordView` puts the record's live responses first
 * (`liveFirst`), so the survey's tab, its strip and Answers, the summary's line and Veritant
 * research take a live response wherever one exists. A survey held only in the recycle bin keeps
 * its tab, and its strip and summary line say so; the strip carries the bin's press (D3).
 *
 * **One Actions menu** carries the page's writes Salesforce puts in its header — Ask registries
 * again, Write a note, New task (§3.1). Each goes to the record tab first, since what it answers
 * is said there (beside each number, in Notes, in Work items); a write about one row stays on its
 * row. An entry the viewer's roles do not include is drawn off with `NotPermitted`'s sentence,
 * never hidden.
 */

/** The hub as a record page draws it: the opened participant's share of every list, the
 *  household left out (it is Related records' to name), and its responses live first
 *  (`liveFirst`) — so every reader that takes a survey's first response takes its current one. */
export function recordView(hub: IAdminParticipantHub): IAdminParticipantHub {
  const id = hub.participant.participantId;
  return {
    ...hub,
    household: [],
    responses: liveFirst(hub.responses.filter((row) => row.participant.participantId === id)),
    notes: hub.notes.filter((note) => note.participantId === id),
    pendingLookups: hub.participant.pendingLookups,
    disclosures: hub.disclosures.filter((entry) => entry.participantId === id),
    internal: hub.internal?.filter((record) => record.participantId === id)
  };
}

/** The record pages (SR-391): a person's, a team's, a firm's, and any other organisation's — an
 *  `org` holding neither survey (a firm registered and not started, say). */
export type RecordLayout = 'person' | 'team' | 'firm' | 'org';

/**
 * The page a participant opens on. A `person` by its kind, which every survey-api has sent; a
 * team, a firm or another organisation by survey-api's `recordType` alone — the wire's word, never
 * guessed here from the surveys held. A survey-api before SR-391's B1 sends none, and a
 * non-person then keeps the household's page: the one place that page is still drawn.
 */
export function recordLayoutOf(participant: IAdminParticipantDetail): Nullable<RecordLayout> {
  if (participant.kind === 'person') return 'person';
  const type = participant.recordType;
  return type === 'team' || type === 'firm' || type === 'org' ? type : null;
}

/** The record tab's label on each page: its record type's words. */
export const RECORD_TAB_LABEL: Record<RecordLayout, string> = RECORD_TYPE_WORDS;

/** One survey tab: the survey, and the record's current response of it — its newest live one,
 *  else its newest (`liveFirst`). A survey held only in the recycle bin keeps its tab. */
export interface ISurveyTab {
  code: string;
  name: string;
  latest: IOversightResponseRow;
}

/** The person's surveys, each once, by code — Veritant research's parts' order, and a stable one:
 *  a tab must not move under the reader when a re-read brings a respondent's write. Each tab's
 *  response is the first of its survey, which is its current one in `recordView`'s hub. */
export function surveyTabsOf(hub: IAdminParticipantHub): ISurveyTab[] {
  const id = hub.participant.participantId;
  return surveysOf(hub.responses, id)
    .sort()
    .map((code) => {
      const latest = hub.responses.find(
        (row) => row.participant.participantId === id && row.survey.code === code
      )!;
      return { code, name: latest.survey.name, latest };
    });
}

/** Related records' anchor. */
const RELATED_ANCHOR = 'related';

/** The sections every record tab draws after its own, in U1's order — the logins and the
 *  registration runs are the Account tab's (`ACCOUNT_TAB`). No Surveys: each survey is a tab of
 *  its own and the summary lists them with their status, so a third list of the same responses
 *  was a duplicate (SR-407). */
const COMMON_TAIL = [
  'records',
  'disclosures',
  'work-items',
  'identity',
  'answers',
  'activity',
  'tasks',
  'notes'
];

/** The Account tab's sections (SR-391, the client call of 2026-10-08): who signs in for the record
 *  and how — Account & access, its Sign-in cards included — and the registration runs that
 *  made those logins. */
const ACCOUNT_TAB = ['access', 'registrations'];

/** The Account tab's label. */
export const ACCOUNT_TAB_LABEL = 'Account';

/** The Account tab's sections, from the hub's registry. */
export function accountSections(): IHubSection[] {
  const byKey = new Map(hubSections().map((section) => [section.key, section]));
  return ACCOUNT_TAB.map((key) => byKey.get(key)!);
}

/**
 * Each record tab's sections, by the hub registry's keys: Overview and Related records first, then
 * what makes the record what it is — a team's Advisors (the `Teams__c` page's Advisor Info) and its
 * routing, a firm's SEC ADV register entry and its Principals (`RIA_Ranking__c`'s Page 1 and
 * Principals Info) — then U1's platform sections. Veritant research is never a record tab's: it is
 * every survey tab's.
 */
const RECORD_TABS: Record<RecordLayout, string[]> = {
  person: ['overview', 'related', ...COMMON_TAIL],
  team: ['overview', 'related', 'roster', 'team', ...COMMON_TAIL],
  firm: ['overview', 'related', 'sec-adv', 'roster', ...COMMON_TAIL],
  // An organisation holding neither survey has no slots and no routing; a register firm its
  // numbers name is still the SEC's entry for it.
  org: ['overview', 'related', 'sec-adv', ...COMMON_TAIL]
};

/** The roster's name on each page — the slots Salesforce named so on that object. */
const ROSTER_LABEL: Record<RecordLayout, string> = {
  person: 'Advisors & principals',
  team: 'Advisors',
  firm: 'Principals',
  org: 'Advisors & principals'
};

/** The sections of one tab — the record tab's, or a survey tab's: its latest response's answers
 *  (SR-431), its responses, then its staff fields with their placements and figures (Veritant
 *  research narrowed to the survey). */
export function recordSections(
  layout: RecordLayout,
  surveyCode: string | undefined
): IHubSection[] {
  const byKey = new Map(hubSections().map((section) => [section.key, section]));
  if (surveyCode !== undefined) {
    return [SURVEY_ANSWERS, SURVEY_RESPONSES, byKey.get('research')!];
  }
  return RECORD_TABS[layout].map((key) => {
    if (key === 'related') return RELATED;
    if (key === 'sec-adv') return SEC_ADV;
    const section = byKey.get(key)!;
    if (key === 'answers' && RECORD_TABS[layout].includes('sec-adv')) {
      return {
        ...section,
        render: (ctx, anchor) => section.render({ ...ctx, secAdvElsewhere: true }, anchor)
      };
    }
    if (key !== 'roster') return section;
    const label = ROSTER_LABEL[layout];
    return {
      ...section,
      label,
      render: (ctx, anchor) => section.render({ ...ctx, rosterTitle: label }, anchor)
    };
  });
}

/** The survey tab's responses: the person's of that survey. */
const responsesOf = ({ hub, surveyCode }: IHubContext) =>
  hub.responses.filter((row) => row.survey.code === surveyCode);

const SURVEY_RESPONSES: IHubSection = {
  key: 'survey-responses',
  anchor: 'survey-responses',
  label: 'Responses',
  count: (ctx) => responsesOf(ctx).length,
  // One response is the strip's, said there with its progress; a one-row table under it was the
  // same response twice (SR-407). Two or more — a survey answered across cycles — keep the list.
  drawn: (ctx) => responsesOf(ctx).length > 1,
  render: (ctx, anchor) => (
    <SurveysSection
      title="Responses"
      responses={responsesOf(ctx)}
      multi={false}
      anchor={anchor}
    />
  )
};

/** The survey tab's answers (SR-431): the current response's — the strip's, first in
 *  `recordView`'s live-first order — in the survey's order. */
const SURVEY_ANSWERS: IHubSection = {
  key: 'survey-answers',
  anchor: 'survey-answers',
  label: 'Answers',
  drawn: (ctx) => responsesOf(ctx).length > 0,
  render: (ctx, anchor) => {
    const [current, ...others] = responsesOf(ctx);
    return (
      <SurveyAnswersSection
        current={current}
        others={others.length}
        reread={ctx.reread}
        anchor={anchor}
      />
    );
  }
};

/** Related records is drawn from the hub as read (`asRead`) — the household — not the person's
 *  view of it. */
const RELATED: IHubSection = {
  key: 'related',
  anchor: RELATED_ANCHOR,
  label: 'Related records',
  render: ({ hub, asRead, search, written }, anchor) => (
    <RelatedRecordsSection
      hub={hub}
      household={asRead}
      search={search}
      written={written}
      anchor={anchor}
    />
  )
};

/**
 * Who else this person's record is tied to, each a link to its own page: their teams and firms —
 * the hub's `affiliations` (SR-414, the plan's D23: reached by their login or by a roster slot
 * resolved to them, each with its responses) — and every other participant an account of theirs
 * acts for (the household, one level out, as survey-api groups it) with its kind and surveys,
 * **less the teams and firms already named**: a team the account acts for is in both lists, and is
 * drawn once. A team that names the person but whose slot nobody resolved is not here — that is
 * the team's page to decide (SR-387). An API before SR-414 sends no `affiliations`, and the teams
 * and firms are then each person's roster read (`MembershipsFact`, the plan's D4), never "named on
 * no team".
 */
function RelatedRecordsSection({
  hub,
  household,
  search,
  written,
  anchor
}: {
  hub: IAdminParticipantHub;
  household: IAdminParticipantHub;
  search: string;
  written: number;
  anchor: string;
}) {
  const affiliations = hub.affiliations;
  const others = household.household.filter(
    (member) =>
      !affiliations?.some((affiliation) => affiliation.participantId === member.participantId)
  );
  // What the record is called in a sentence: its layout's own word.
  const noun = recordNoun(recordLayoutOf(hub.participant) ?? 'person');
  const person = hub.participant.kind === 'person';
  // Without the hub's affiliations a person's teams and firms take a read; until it lands the
  // section is not called empty.
  const [read, setRead] = React.useState<number | undefined>(undefined);
  const named = affiliations !== undefined ? affiliations.length : read;
  const empty = others.length === 0 && (!person || named === 0);
  return (
    <Section
      title="Related records"
      anchor={anchor}
      empty={empty}
      description={
        hub.participant.kind === 'person'
          ? 'The teams and firms this person is named on, and the records their login also ' +
            'acts for.'
          : 'The records this one’s login also acts for.'
      }
    >
      <CardContent className="grid gap-6 text-sm">
        {/* Only a person is named in a roster's slots; a team's or firm's own slots are its
            Advisors or Principals. */}
        {person &&
          (affiliations !== undefined ? (
            <AffiliationsFact
              participantId={hub.participant.participantId}
              affiliations={affiliations}
              search={search}
            />
          ) : (
            <MembershipsFact
              participantId={hub.participant.participantId}
              search={search}
              written={written}
              onCount={setRead}
            />
          ))}
        <div data-shared-login className="grid gap-1">
          <p className="text-xs font-medium text-muted-foreground">Same login</p>
          {others.length === 0 ? (
            <p>
              {absent(
                hub.participant.accounts.length === 0
                  ? `No account acts for this ${noun}.`
                  : household.household.length > 0
                    ? 'Nobody else — the other records their login acts for are the teams and ' +
                      'firms above.'
                    : `No account acting for this ${noun} acts for anybody else.`
              )}
            </p>
          ) : (
            <ul aria-label="Same login" className="grid gap-1">
              {others.map((member) => {
                const surveys = surveysOf(household.responses, member.participantId);
                return (
                  <li
                    key={member.participantId}
                    data-member={member.participantId}
                    className="flex flex-wrap items-center gap-2"
                  >
                    <ParticipantLink participant={member} search={search} />
                    <Badge variant="soft">{member.kind}</Badge>
                    <span className="text-muted-foreground">
                      {surveys.length === 0 ? 'No response held' : surveys.join(' · ')}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </CardContent>
    </Section>
  );
}

/** A survey tab's strip, over its sections: the survey's current response — its cycle and status,
 *  a link to it, where it sits in the recycle bin, and its recycle bin press (SR-470's D3), whose
 *  landing re-reads the hub so the tab, this strip and the summary follow — and the survey's
 *  headline staff values (Approval status, Quality rating, Travel), which the header drew for
 *  every survey at once before the tabs. */
export function SurveyStrip({
  hub,
  tab,
  reread
}: {
  hub: IAdminParticipantHub;
  tab: ISurveyTab;
  reread: () => Promise<boolean>;
}) {
  const { latest } = tab;
  return (
    <section
      aria-label={`${tab.name} at a glance`}
      data-survey-strip={tab.code}
      className="grid gap-2 rounded-lg border border-border bg-card p-4 text-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{tab.name}</span>
          <span className="text-muted-foreground">{latest.cycle.name}</span>
          <ResponseStatusChip status={latest.status} />
          <Progress answered={latest.answered} askable={latest.askable} />
          <span className="text-muted-foreground">changed {formatDateTime(latest.updatedAt)}</span>
          <Link
            to={responsePath(latest.responseId)}
            className="text-[var(--color-sapphire)] underline-offset-4 hover:underline"
          >
            Open the response
          </Link>
        </p>
        {/* Keyed by the survey: the strip itself is kept across tabs, and what a press was told
            belongs to its own survey's tab. */}
        <RecyclePress
          key={tab.code}
          response={{
            responseId: latest.responseId,
            status: latest.status,
            etag: `"${latest.revision}"`,
            recycled: latest.recycled,
            subject: subjectOf(latest)
          }}
          onMoved={reread}
          onReread={reread}
        />
      </div>
      {latest.status === 'recycled' && (
        <p data-in-the-bin className="text-muted-foreground">
          {inTheBin(latest.recycled)}
        </p>
      )}
      <ul aria-label="Headline values" className="flex flex-wrap items-center gap-2">
        <ResearchHeadline hub={hub} surveyCode={tab.code} />
      </ul>
    </section>
  );
}

/** The firm page's SEC ADV section (SR-391, AC2): the register's entry for each firm survey-api
 *  matched the firm's numbers to — read only under `registry.browse`, the register's own
 *  permission; which firms matched, and by which number, is said under Registry answers. */
const SEC_ADV: IHubSection = {
  key: 'sec-adv',
  anchor: 'sec-adv',
  label: 'SEC ADV',
  // A card read without `secAdvFirms` (a survey-api before SR-214) asked the register nothing:
  // no section, never "no firm" — Registry answers' rule.
  drawn: ({ hub }) => hub.participant.secAdvFirms !== undefined,
  render: ({ hub }, anchor) => (
    <SecAdvSection
      noun={recordNoun(recordLayoutOf(hub.participant) ?? 'firm')}
      firms={hub.participant.secAdvFirms ?? []}
      identifiers={hub.participant.identifiers}
      anchor={anchor}
    />
  )
};

function SecAdvSection({
  noun,
  firms,
  identifiers,
  anchor
}: {
  /** What the record is called — "firm", or "organization" for an org's page. */
  noun: string;
  firms: IAdminParticipantSecAdvFirm[];
  identifiers: IAdminParticipantIdentifier[];
  anchor: string;
}) {
  const canBrowse = useCan()('registry.browse');
  const looked = identifiers.some((identifier) => SEC_ADV_MATCHED_ID_TYPES.has(identifier.idType));
  return (
    <Section
      title="SEC ADV"
      anchor={anchor}
      description={
        `The SEC’s register entry for this ${noun}, as the system matched it by its SEC ` +
        'number or firm CRD. A match is not a verification.'
      }
    >
      <CardContent className="grid gap-6 text-sm">
        {firms.length === 0 ? (
          <p>
            {absent(
              looked
                ? `No firm in the register has this ${noun}’s SEC number or firm CRD — the register ` +
                    'may be empty or out of date; Registry answers says which numbers were ' +
                    'looked up.'
                : `This ${noun} holds no SEC number or firm CRD to look the register up by.`
            )}
          </p>
        ) : (
          firms.map((firm) => (
            <div
              key={firm.firmCrd}
              role="group"
              aria-label={firm.legalName}
              data-sec-adv-firm={firm.firmCrd}
              className="grid gap-3"
            >
              <h3 className="flex flex-wrap items-baseline gap-x-2 text-sm font-semibold text-[var(--color-royal-blue)]">
                {canBrowse ? <FirmLink firm={firm} /> : firm.legalName}
                <span className="font-normal text-muted-foreground">
                  CRD {firm.firmCrd} · SEC {firm.secNumber} · {firm.firmType}
                </span>
              </h3>
              {canBrowse ? (
                <SecAdvRegisterEntry firmCrd={firm.firmCrd} />
              ) : (
                <p className="text-muted-foreground">
                  The register’s summary and custodians are on its card, which your Team Portal
                  roles do not open.
                </p>
              )}
            </div>
          ))
        )}
      </CardContent>
    </Section>
  );
}

/** What a record is called in a sentence. */
function recordNoun(layout: RecordLayout): string {
  return layout === 'org' ? 'organization' : layout;
}

/** A survey tab whose survey the person does not hold — a copied link, or a survey the person's
 *  responses left. Said, with the way to the record. */
export function NoSuchSurvey({
  code,
  recordTo,
  layout
}: {
  code: string;
  recordTo: string;
  layout: RecordLayout;
}) {
  return (
    <p className="text-sm">
      {absent(`This ${recordNoun(layout)} holds no ${code} response.`)}{' '}
      <Link to={recordTo} className="text-[var(--color-sapphire)] underline-offset-4 hover:underline">
        Back to the record
      </Link>
    </p>
  );
}

/** What the Actions menu can ask of the page — a team's page adds its two writes, which open their
 *  own control in the Team section (`routing`, `archive`). */
export type RecordAction = 'ask' | 'note' | 'task' | 'routing' | 'archive' | 'escalate' | 'recycle';

/**
 * The page's Actions menu (§3.1): the record page's writes Salesforce puts in its header. The menu
 * says what each would do and why one is off; the page runs it (`onAct`) on the record tab.
 */
export function ActionsMenu({
  asking,
  canAnnotate,
  onAct,
  team,
  canDecide,
  recyclable = 0
}: {
  asking: IAskEveryNumber;
  canAnnotate: boolean;
  /** `work_items.decide` — Escalate's permission (SR-400). */
  canDecide: boolean;
  onAct: (action: RecordAction) => void;
  /** A team's page: its routing and archive, as the Team section draws them — `routed` whether it
   *  holds a routing to decide on, `drawn` whether the section is drawn at all (and `archived`
   *  which of Archive and Restore it offers). An entry the section does not draw is not offered. */
  team?: { routed: boolean; drawn: boolean; archived: boolean };
  /** The surveys whose current response can be moved to the recycle bin (SR-490) — the entry
   *  opens a survey strip's press, after a choice where there are several. */
  recyclable?: number;
}) {
  // The entry chosen, run once the menu has closed: an action that puts the cursor somewhere (the
  // note's box, the task's subject) would otherwise lose it to the menu's focus trap, and then to
  // the trigger the menu hands the focus back to.
  const chosen = React.useRef<Nullable<RecordAction>>(null);
  const act = (action: RecordAction) => {
    chosen.current = action;
  };
  const askReason = !canAnnotate
    ? null
    : asking.covered === 0
      ? 'No number here can be asked of a registry again.'
      : asking.targets.length === 0
        ? 'A lookup is already waiting on every number a registry can be asked about again.'
        : `${asking.targets.length === 1 ? 'One number' : `${asking.targets.length} numbers`} to ` +
          'ask about — what each is told is said beside it, under Identity & numbers.';

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        // A choice belongs to the opening it was made in.
        if (open) chosen.current = null;
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button type="button" size="sm" className="gap-1.5">
          Actions
          <ChevronDown className="size-4" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-72"
        onCloseAutoFocus={(event) => {
          const action = chosen.current;
          chosen.current = null;
          if (action === null) return;
          event.preventDefault();
          onAct(action);
        }}
      >
        <DropdownMenuItem
          disabled={!canAnnotate || asking.targets.length === 0 || asking.askingAll}
          onSelect={() => act('ask')}
          className="grid items-start gap-0.5"
        >
          <span>{asking.askingAll ? 'Asking…' : 'Ask registries again'}</span>
          {askReason !== null && (
            <span className="text-xs text-muted-foreground">{askReason}</span>
          )}
          {!canAnnotate && <NotPermitted what="asking registries again" className="text-xs" />}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => act('note')}>Write a note</DropdownMenuItem>
        <DropdownMenuItem
          disabled={!canAnnotate}
          onSelect={() => act('task')}
          className="grid items-start gap-0.5"
        >
          <span>New task</span>
          {!canAnnotate && <NotPermitted what={NOT_PERMITTED_RAISE} className="text-xs" />}
        </DropdownMenuItem>
        {/* SR-400: the compliance queue's escalation, from the record (escalate.tsx). */}
        <DropdownMenuItem
          disabled={!canDecide}
          onSelect={() => act('escalate')}
          className="grid items-start gap-0.5"
        >
          <span>Escalate…</span>
          {!canDecide && <NotPermitted what={NOT_PERMITTED_DECIDE} className="text-xs" />}
        </DropdownMenuItem>
        {/* SR-490: the survey strip's recycle press, reached from the record (recycle-action.tsx). */}
        <DropdownMenuItem
          disabled={!canAnnotate || recyclable === 0}
          onSelect={() => act('recycle')}
          className="grid items-start gap-0.5"
        >
          <span>Move a response to the recycle bin…</span>
          {canAnnotate && recyclable === 0 && (
            <span className="text-xs text-muted-foreground">
              No response here to move.
            </span>
          )}
          {!canAnnotate && (
            <NotPermitted
              what="moving a response to the recycle bin or restoring it"
              className="text-xs"
            />
          )}
        </DropdownMenuItem>
        {team && (
          <>
            {/* Each opens its control in the Team section, where its confirmation, its rules and
                what it is told live; whether the reader may press it is said there. */}
            {team.routed && (
              <DropdownMenuItem
                disabled={!canAnnotate}
                onSelect={() => act('routing')}
                className="grid items-start gap-0.5"
              >
                <span>Decide routing…</span>
                {!canAnnotate && (
                  <NotPermitted what="deciding a team’s routing" className="text-xs" />
                )}
              </DropdownMenuItem>
            )}
            {team.drawn && (
              <DropdownMenuItem
                disabled={!canAnnotate}
                onSelect={() => act('archive')}
                className="grid items-start gap-0.5"
              >
                <span>{team.archived ? 'Restore team…' : 'Archive team…'}</span>
                {!canAnnotate && (
                  <NotPermitted
                    what={team.archived ? 'restoring a team' : 'archiving a team'}
                    className="text-xs"
                  />
                )}
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
